import React from 'react';
import { DialogShell } from './ui/DialogShell';
import { X, Package, User, CreditCard, Truck, Calendar, MapPin, FileText, Edit2, Trash2, CalendarClock } from 'lucide-react';
import { VentaActividad, Miembro, ProductoActividad } from '../lib/supabase';
import { formatDate, formatCurrency } from '../utils/formatters';

interface DetallePedidoModalProps {
  isOpen: boolean;
  onClose: () => void;
  pedido: VentaActividad;
  miembros: Miembro[];
  productos: ProductoActividad[];
  onEdit?: () => void;
  onDelete?: () => void;
  onRegistrarPago?: () => void;
  onRegistrarEntrega?: () => void;
}

export const DetallePedidoModal: React.FC<DetallePedidoModalProps> = ({
  isOpen,
  onClose,
  pedido,
  miembros,
  productos,
  onEdit,
  onDelete,
  onRegistrarPago,
  onRegistrarEntrega
}) => {
  if (!isOpen) return null;

  const getNombreCliente = (): string => {
    if (pedido.miembro_id) {
      const miembro = miembros.find(m => m.id === pedido.miembro_id);
      return miembro ? `${miembro.nombres} ${miembro.apellidos}` : 'Miembro';
    }
    return pedido.nombre_externo || 'Cliente';
  };

  const getNombreReferido = (): string | null => {
    if (!pedido.miembro_referido_id) return null;
    const miembro = miembros.find(m => m.id === pedido.miembro_referido_id);
    return miembro ? `${miembro.nombres} ${miembro.apellidos}` : null;
  };

  const getProducto = () => {
    return productos.find(p => p.id === pedido.producto_id);
  };


  const getEstadoPagoTexto = () => {
    if (pedido.estado_pago === 'pagado') return 'Pagado completamente';
    if (pedido.estado_pago === 'pago_parcial') return 'Pago parcial';
    return 'Pendiente de pago';
  };

  const getEstadoEntregaTexto = () => {
    if (pedido.estado_entrega === 'entregado') return 'Entregado completamente';
    if (pedido.estado_entrega === 'entrega_parcial') return 'Entrega parcial';
    return 'Pendiente de entrega';
  };

  const producto = getProducto();
  const nombreReferido = getNombreReferido();

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      header={(
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Package className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Detalle del Pedido</h2>
              <p className="text-blue-100 text-sm">Información completa</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      )}
    >
          {/* Contenido */}
          <div className="space-y-6">
            {/* Cliente */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-5 h-5 text-slate-600" />
                <h3 className="font-bold text-slate-900 uppercase text-sm">Cliente</h3>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-lg font-semibold text-slate-800">{getNombreCliente()}</p>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(pedido.monto_total)}</p>
              </div>
              {nombreReferido && (
                <p className="text-sm text-slate-600 mt-2">
                  Referido por: <span className="font-medium text-slate-800">{nombreReferido}</span>
                </p>
              )}
            </div>

            {/* Producto */}
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-5 h-5 text-purple-600" />
                <h3 className="font-bold text-purple-900 uppercase text-sm">Producto</h3>
              </div>
              <p className="text-lg font-semibold text-slate-800">
                {producto?.nombre_producto || 'Producto'} <span className="text-slate-600">x {pedido.cantidad} unidades</span>
              </p>
              <p className="text-sm text-slate-600 mt-1">
                Precio unitario: {formatCurrency(pedido.monto_total / pedido.cantidad)}
              </p>
            </div>

            {/* Pago */}
            <div className="bg-emerald-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="w-5 h-5 text-emerald-600" />
                <h3 className="font-bold text-emerald-900 uppercase text-sm">Pago</h3>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Estado:</span>
                  <span className="font-semibold text-slate-800">{getEstadoPagoTexto()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Monto pagado:</span>
                  <span className="font-semibold text-emerald-700">{formatCurrency(pedido.monto_pagado)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Saldo pendiente:</span>
                  <span className="font-semibold text-slate-800">{formatCurrency(pedido.monto_total - pedido.monto_pagado)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Modo de pago:</span>
                  <span className="font-medium text-slate-800 capitalize">{pedido.modo_pago}</span>
                </div>
              </div>
            </div>

            {/* Entrega */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Truck className="w-5 h-5 text-blue-600" />
                <h3 className="font-bold text-blue-900 uppercase text-sm">Entrega</h3>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Estado:</span>
                  <span className="font-semibold text-slate-800">{getEstadoEntregaTexto()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Cantidad entregada:</span>
                  <span className="font-semibold text-blue-700">{pedido.cantidad_entregada} de {pedido.cantidad} unidades</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Pendiente:</span>
                  <span className="font-semibold text-slate-800">{pedido.cantidad - pedido.cantidad_entregada} unidades</span>
                </div>
                <div className="border-t border-blue-200 pt-2 mt-2">
                  <div className="flex items-start gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-slate-600 mb-1">Forma de entrega:</p>
                      <p className="font-medium text-slate-800">
                        {pedido.forma_entrega === 'despacho' 
                          ? pedido.direccion_despacho || 'Sin dirección especificada'
                          : 'Retiro en sede'}
                      </p>
                    </div>
                  </div>
                  {(pedido.fecha_entrega || pedido.rango_horario_retiro_despacho) && (
                    <div className="flex items-start gap-2">
                      <CalendarClock className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-slate-600 mb-1">Fecha y hora programada:</p>
                        <p className="font-medium text-slate-800">
                          {pedido.fecha_entrega && formatDate(pedido.fecha_entrega)}
                          {pedido.fecha_entrega && pedido.rango_horario_retiro_despacho && ' • '}
                          {pedido.rango_horario_retiro_despacho}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Información adicional */}
            <div className="border-t border-slate-200 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-slate-600" />
                <span className="text-sm text-slate-600">
                  Fecha del pedido: <span className="font-medium text-slate-800">{formatDate(pedido.fecha_venta)}</span>
                </span>
              </div>

              {pedido.notas && (
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-amber-900 mb-1">Notas</p>
                      <p className="text-sm text-slate-700">{pedido.notas}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Botones de acción */}
            {(onEdit || onDelete || onRegistrarPago || onRegistrarEntrega) && (
              <div className="border-t border-slate-200 pt-4 space-y-3">
                {(onEdit || onDelete) && (
                  <div className="grid grid-cols-2 gap-3">
                    {onEdit && (
                      <button
                        onClick={onEdit}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                      >
                        <Edit2 className="w-4 h-4" />
                        Editar Pedido
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={onDelete}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
                      >
                        <Trash2 className="w-4 h-4" />
                        Eliminar Pedido
                      </button>
                    )}
                  </div>
                )}

                {(onRegistrarPago || onRegistrarEntrega) && (
                  <div className="grid grid-cols-2 gap-3">
                    {onRegistrarPago && pedido.estado_pago !== 'pagado' && (
                      <button
                        onClick={onRegistrarPago}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors font-semibold"
                      >
                        <CreditCard className="w-4 h-4" />
                        Registrar Pago
                      </button>
                    )}
                    {onRegistrarEntrega && pedido.estado_entrega !== 'entregado' && (
                      <button
                        onClick={onRegistrarEntrega}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-semibold"
                      >
                        <Truck className="w-4 h-4" />
                        Registrar Entrega
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
    </DialogShell>
  );
};
